import { login, twoFactorAuth, useUser } from "@/api/auth.ts";
import { useGetInstance } from "@/api/instance.ts";
import { useMutation } from "@tanstack/react-query";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { HttpStatusCode } from "axios";
import { fetchMainLanguage, SupportedLocale } from "@/i18n.ts";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [instance, setInstance] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { data: user } = useUser(token);
  const { data: instanceData } = useGetInstance(instance);

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      if (data.status === HttpStatusCode.Ok) {
        const sessionToken = data.token;
        setToken(sessionToken);
        localStorage.setItem("token", sessionToken);
      }
    },
    onError: (error) => {
      console.error("Login failed:", error);
    },
  });

  const twoFactorLoginMutation = useMutation({
    mutationFn: twoFactorAuth,
    onSuccess: (data) => {
      if (data.status === HttpStatusCode.Ok) {
        const sessionToken = data.token;
        setToken(sessionToken);
        localStorage.setItem("token", sessionToken);
      }
    },
  });

  useEffect(() => {
    const storedInstance = localStorage.getItem("instance");
    if (user && !storedInstance) {
      navigate("/instance");
    } else if (user && storedInstance) {
      //navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedInstance = localStorage.getItem("instance");

    if (storedInstance) {
      setInstance(storedInstance);
    }

    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  const handleLogin = async (
    email: string,
    password: string,
    locale: SupportedLocale,
  ) => {
    const response = await loginMutation.mutateAsync({
      email,
      password,
      locale,
    });
    return response;
  };

  const handleTwoFactorLogin = async (
    email: string,
    twoFactorToken: string,
    locale: SupportedLocale,
  ) => {
    const response = await twoFactorLoginMutation.mutateAsync({
      email,
      twoFactorToken,
      locale,
    });
    return response;
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("instance");
    navigate("/login");
  };

  useEffect(() => {
    if (token) {
      fetchMainLanguage();
    }
  }, [token]);

  const checkToken = () => {
    return !!localStorage.getItem("token");
  };

  const checkInstanceAndToken = () => {
    return (
      !!localStorage.getItem("token") && !!localStorage.getItem("instance")
    );
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        instance,
        instanceData,
        setInstance,
        login: handleLogin,
        twoFactorLogin: handleTwoFactorLogin,
        logout,
        checkToken,
        checkInstanceAndToken,
        user: user ? user[0] : null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
